import { useEffect, useState } from "react";
import {
  FileText, Edit3, Trash2, Loader2, CheckCircle, Eye, Save, X, Sparkles, Plus, ExternalLink
} from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const API = BACKEND_URL;

const STATUS_STYLES = {
  draft: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  published: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  archived: "bg-white/5 text-white/40 border-white/10",
};

function EditModal({ post, authToken, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: post.title || "",
    excerpt: post.excerpt || "",
    body: post.body || "",
    featured_image: post.featured_image || "",
    category: post.category || "Platform Updates",
    tags: (post.tags || []).join(", "),
    status: post.status || "draft",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (publishNow = false) => {
    setSaving(true); setError("");
    try {
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        body: form.body,
        featured_image: form.featured_image.trim(),
        category: form.category.trim(),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        status: publishNow ? "published" : form.status,
      };
      if (!payload.title) throw new Error("Title required");
      if (!payload.body) throw new Error("Body required");
      const r = await fetch(`${API}/api/news/${post.post_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Save failed (HTTP ${r.status})`);
      onSaved(publishNow ? "Published" : "Saved");
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="news-edit-modal">
      <div className="bg-gray-950 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-950 border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" />
            <h2 className="text-white font-semibold">Edit News Post</h2>
            <span className={`text-[10px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_STYLES[form.status] || STATUS_STYLES.draft}`}>
              {form.status}
            </span>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white/60 transition" data-testid="news-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-white/40 text-xs font-medium block mb-1.5">Title</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50"
              data-testid="news-edit-title" />
          </div>
          <div>
            <label className="text-white/40 text-xs font-medium block mb-1.5">Excerpt (meta description — 160 chars max)</label>
            <textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value.slice(0, 160))} rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50 resize-y"
              data-testid="news-edit-excerpt" />
            <p className="text-white/30 text-xs mt-1">{form.excerpt.length}/160</p>
          </div>
          <div>
            <label className="text-white/40 text-xs font-medium block mb-1.5">Body (markdown supported)</label>
            <textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={18}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-mono leading-relaxed focus:outline-none focus:border-purple-500/50 resize-y"
              data-testid="news-edit-body" />
            <p className="text-white/30 text-xs mt-1">{form.body.trim().split(/\s+/).filter(Boolean).length} words &middot; {form.body.length} chars</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-white/40 text-xs font-medium block mb-1.5">Category</label>
              <input value={form.category} onChange={(e) => set("category", e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50" />
            </div>
            <div>
              <label className="text-white/40 text-xs font-medium block mb-1.5">Tags (comma-separated)</label>
              <input value={form.tags} onChange={(e) => set("tags", e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50" />
            </div>
          </div>
          <div>
            <label className="text-white/40 text-xs font-medium block mb-1.5">Featured image URL</label>
            <input value={form.featured_image} onChange={(e) => set("featured_image", e.target.value)}
              placeholder="https://..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50" />
            {form.featured_image && (
              <img src={form.featured_image} alt="" className="mt-2 max-h-32 rounded-lg border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />
            )}
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
        </div>
        <div className="sticky bottom-0 bg-gray-950 border-t border-white/10 px-6 py-4 flex flex-col sm:flex-row items-center gap-3 justify-end">
          <a href={`/news/${post.slug}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm transition">
            <Eye className="w-4 h-4" /> Preview
          </a>
          <button onClick={() => save(false)} disabled={saving}
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/80 px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
            data-testid="news-edit-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Draft
          </button>
          <button onClick={() => save(true)} disabled={saving}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
            data-testid="news-edit-publish">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} {form.status === "published" ? "Update" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminNewsTab({ authToken, onMessage }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/news?limit=200`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      const d = await r.json();
      setPosts(Array.isArray(d) ? d : []);
    } catch { setPosts([]); }
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, []);

  const filtered = posts.filter((p) => filter === "all" || p.status === filter);
  const counts = {
    all: posts.length,
    draft: posts.filter((p) => p.status === "draft").length,
    published: posts.filter((p) => p.status === "published").length,
  };

  const onSaved = async (msg) => {
    setEditing(null);
    onMessage?.(msg || "Saved");
    await fetchPosts();
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API}/api/news/${post.post_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error("Delete failed");
      onMessage?.("Deleted");
      await fetchPosts();
    } catch (e) {
      onMessage?.(e.message);
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10" data-testid="admin-news-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" /> News &amp; Spotlights
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Review AI-drafted Station Spotlights and manage all news posts.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "all", label: `All (${counts.all})` },
          { id: "draft", label: `Drafts (${counts.draft})` },
          { id: "published", label: `Published (${counts.published})` },
        ].map((t) => (
          <button key={t.id} onClick={() => setFilter(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
              filter === t.id
                ? "bg-purple-500/20 text-purple-300 border-purple-500/30"
                : "bg-white/5 text-white/50 border-white/5 hover:text-white hover:border-white/15"
            }`}
            data-testid={`news-filter-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/40 text-sm">No posts match this filter.</p>
          {counts.draft === 0 && counts.all === 0 && (
            <p className="text-white/30 text-xs mt-2">Tip: generate a Station Spotlight from the Directory tab to get started.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="news-list">
          {filtered.map((post) => {
            const isSpotlight = post.category === "Station Spotlight" || (post.tags || []).includes("spotlight");
            const statusCls = STATUS_STYLES[post.status] || STATUS_STYLES.draft;
            return (
              <div key={post.post_id} className="bg-white/[0.03] border border-white/5 rounded-xl p-4 hover:border-white/10 transition" data-testid={`news-row-${post.post_id}`}>
                <div className="flex items-start gap-4">
                  {post.featured_image ? (
                    <img src={post.featured_image} alt="" className="w-16 h-16 rounded-lg object-cover bg-white/5 flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-white/20" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-white font-semibold text-sm truncate">{post.title}</h3>
                      {isSpotlight && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-purple-300 bg-purple-500/15 border border-purple-500/20 px-1.5 py-0.5 rounded-full">
                          <Sparkles className="w-2.5 h-2.5" /> Spotlight
                        </span>
                      )}
                      <span className={`text-[10px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 ${statusCls}`}>
                        {post.status}
                      </span>
                    </div>
                    <p className="text-white/45 text-xs line-clamp-2 mb-2">{post.excerpt || post.body?.slice(0, 160) || ""}</p>
                    <div className="flex items-center gap-3 text-[11px] text-white/30 flex-wrap">
                      <span>{post.category}</span>
                      <span>&middot;</span>
                      <span>{post.created_at ? new Date(post.created_at).toLocaleDateString() : ""}</span>
                      {post.author && <><span>&middot;</span><span>by {post.author}</span></>}
                      <span>&middot;</span>
                      <a href={`/news/${post.slug}`} target="_blank" rel="noopener noreferrer"
                        className="text-white/40 hover:text-white inline-flex items-center gap-1 transition">
                        <ExternalLink className="w-3 h-3" /> /news/{post.slug}
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                  <button onClick={() => setEditing(post)}
                    className="flex items-center gap-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 px-3 py-1.5 rounded-lg text-xs font-medium transition"
                    data-testid={`news-edit-${post.post_id}`}>
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </button>
                  {post.status === "draft" && (
                    <button onClick={() => setEditing(post)}
                      className="flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-lg text-xs font-medium transition"
                      data-testid={`news-review-${post.post_id}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Review &amp; Publish
                    </button>
                  )}
                  <a href={`/news/${post.slug}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white/50 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium transition">
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </a>
                  <button onClick={() => remove(post)}
                    className="flex items-center gap-1.5 bg-white/5 hover:bg-red-500/10 text-white/30 hover:text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition ml-auto"
                    data-testid={`news-delete-${post.post_id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <EditModal post={editing} authToken={authToken} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
